/**
 * Format utilities for dates
 * Converts between YYYY-MM-DD (ISO) and DD/MM/YYYY (display) formats
 */

/**
 * Format date from ISO string to DD/MM/YYYY
 * @param {string|Date} date - ISO date string or Date object
 * @returns {string} Formatted date as DD/MM/YYYY or empty string if invalid
 */
export function formatToDisplayDate(date) {
  if (!date) return '';
  
  try {
    const d = typeof date === 'string' ? new Date(date + 'T00:00:00Z') : new Date(date);
    if (isNaN(d)) return '';
    
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year = d.getUTCFullYear();
    
    return `${day}/${month}/${year}`;
  } catch {
    return '';
  }
}

/**
 * Convert display date from DD/MM/YYYY to ISO YYYY-MM-DD
 * @param {string} displayDate - Date string in DD/MM/YYYY format
 * @returns {string} ISO format YYYY-MM-DD or empty string if invalid
 */
export function formatToISODate(displayDate) {
  if (!displayDate) return '';
  
  try {
    const parts = displayDate.split('/');
    if (parts.length !== 3) return '';
    
    const day = parts[0].padStart(2, '0');
    const month = parts[1].padStart(2, '0');
    const year = parts[2];
    
    // Validate date
    const d = new Date(`${year}-${month}-${day}T00:00:00Z`);
    if (isNaN(d)) return '';
    
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
}

/**
 * Check if a date string is in ISO format (YYYY-MM-DD)
 * @param {string} dateStr - Date string to check
 * @returns {boolean}
 */
export function isISOFormat(dateStr) {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
}

/**
 * Check if a date string is in display format (DD/MM/YYYY)
 * @param {string} dateStr - Date string to check
 * @returns {boolean}
 */
export function isDisplayFormat(dateStr) {
  return /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr);
}

/**
 * Parse a display date string to ISO format, or return an empty string if invalid.
 * Accepts DD/MM/YYYY or already ISO YYYY-MM-DD strings.
 * @param {string} value
 * @returns {string}
 */
export function parseDisplayDate(value) {
  if (!value) return '';
  if (isISOFormat(value)) return value;
  return formatToISODate(value);
}
